import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Grid, Typography } from '@mui/material';
import { useConfigurationLicence } from '../hooks';
import { Accordion } from 'components/molecules/Accordion';
import { EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { LicenceTemplateApplicationProcessAddTask } from '../LicenceTemplateApplicationProcessAddTask';
import Button from 'components/atoms/Button';
import { Alert } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { clearAlertTimeout, EAlertTime } from 'utils/date';
import { useParams } from 'react-router-dom';
import { EPerPages, ISortOptions } from 'models/table';
import { Loader } from 'components/atoms/Loader';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { Table } from 'components/molecules/Table';
import { EDictionaryTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { Pagination } from 'components/molecules/Pagination';
import { ApplicationProcessTaskTableColumns, mapApplicationProcessTask } from '../helper';
import { CardList } from 'components/molecules/CardList';
import ApplicationProcessTaskTableCard from '../ApplicationProcessTaskTableCard';
import { FormModal } from 'components/molecules/Modal';
import { EPermission } from 'models/permissions';

const LicenceTemplateApplicationProcessTasks = () => {
  const { t } = useTranslations();
  const {
    handleGetapplicationProccessTask,
    applicationProccessTasks,
    handleDeleteApplicationProccessTask,
    paginatorApplicationProccessTasks,
    loadDictionaries,
    task,
  } = useConfigurationLicence();
  const { isDesktop } = useThemeBreakpoint();
  const { selectedLanguage } = useLang();
  const { processId = '' } = useParams<{ processId: string }>();
  const { clearAlert, taskAlert } = useAlert();
  const [isAddTaskModal, setIsAddTaskModal] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<EPerPages>(EPerPages.perPage25);
  const [sort, setSort] = useState<ISortOptions | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [applicationProcessTaskDelete, setApplicationProcessTaskDelete] = useState<
    string | null
  >();
  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.task]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await handleGetapplicationProccessTask(processId, currentPage, perPage, sort);
      setIsLoading(false);
    })();
  }, [currentPage, perPage, sort]);

  useEffect(
    () => () => {
      clearAlertTimeout(clearAlert, ActionTypes.CLEAR_TRANSACTION_ALERT, EAlertTime.LONG);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const reloadList = async () => {
    await handleGetapplicationProccessTask(processId, currentPage, perPage, sort);
  };

  return (
    <>
      {isAddTaskModal && (
        <LicenceTemplateApplicationProcessAddTask
          addSuccess={reloadList}
          handleClose={() => setIsAddTaskModal(false)}
          id={processId}
        />
      )}
      <Accordion
        title={t('licence.tasks.label')}
        name="tasks"
        key="price"
        alwaysExpanded
        hideExpandIcon
      >
        <Grid
          container
          sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}
        >
          <Grid>
            <Typography variant="body2">{t('licence.tasks.text')}</Typography>
          </Grid>
          <Grid sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button
              label={t('licence.addTask.label')}
              id="addTasks"
              variant={EButtonVariants.contained}
              onClick={() => setIsAddTaskModal(true)}
              icon={EIconTypes.plus}
              permission={EPermission.MANAGE_WEAPON_LICENCE_CONFIGURATOR}
              size="medium"
              fullWidth
              sx={{ minWidth: { sm: '120px' } }}
            />
          </Grid>
        </Grid>
        <FormModal
          open={!!applicationProcessTaskDelete}
          onClickClose={() => {
            setApplicationProcessTaskDelete(null);
          }}
          onClickApprove={() => {
            if (applicationProcessTaskDelete) {
              handleDeleteApplicationProccessTask(applicationProcessTaskDelete).then(() =>
                reloadList(),
              );
              setApplicationProcessTaskDelete(null);
            }
          }}
          title={t('licenceConfigurator.task.delete.text')}
          approveButtonLabel="general.yes.label"
          cancelButtonLabel="general.no.label"
        />
        {taskAlert && (
          <Grid sx={{ mb: 2 }}>
            <Grid size={{ xs: 12 }}>
              <Alert text={taskAlert.text} variant={taskAlert.variant} />
            </Grid>
          </Grid>
        )}
        {isLoading ? (
          <Loader isLoading={true} />
        ) : (
          <>
            {isDesktop ? (
              <Table
                columns={ApplicationProcessTaskTableColumns({
                  handleDelete: (id) => {
                    setApplicationProcessTaskDelete(id);
                  },
                })}
                dataSource={mapApplicationProcessTask(applicationProccessTasks, task)}
                onSort={(it) => {
                  setSort(it);
                }}
              />
            ) : (
              <CardList
                items={mapApplicationProcessTask(applicationProccessTasks, task)}
                render={(data, index) => (
                  <ApplicationProcessTaskTableCard
                    data={data}
                    key={index}
                    handleDelete={(id: string) => setApplicationProcessTaskDelete(id)}
                  />
                )}
              />
            )}

            {paginatorApplicationProccessTasks && (
              <Pagination
                count={paginatorApplicationProccessTasks?.totalElements}
                onChangePage={(page) => setCurrentPage(page)}
                current={currentPage}
                perPage={perPage}
                onChangePerPage={(value) => setPerPage(value)}
                isLoading={isLoading}
              />
            )}
          </>
        )}
      </Accordion>
    </>
  );
};

export { LicenceTemplateApplicationProcessTasks };
